import { Badge, Button, useToast } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { FaPlay, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

import updateSound from 'assets/sounds/update-sound.mp3';
import { getPendingTransportRequestUatAlert } from 'data/repositories/TransportRequest';

const REMINDER_ENABLED_KEY = 'transport-request-uat-reminder-enabled';
const CHECKED_SLOT_PREFIX = 'transport-request-uat-reminder-checked';
const BANGKOK_OFFSET_HOURS = 7;
const LATE_GRACE_MS = 2 * 60 * 1000;
const REMINDER_MINUTES_BEFORE_UPDATE = 10;

const UPDATE_TIMES = [
    { label: '10:00', hour: 10, minute: 0 },
    { label: '13:30', hour: 13, minute: 30 },
    { label: '15:00', hour: 15, minute: 0 },
    { label: '17:00', hour: 17, minute: 0 },
    { label: '18:00', hour: 18, minute: 0 },
    { label: '21:30', hour: 21, minute: 30 }
];

type ReminderSlot = {
    key: string;
    label: string;
    timestamp: number;
};

const padDatePart = (value: number) => String(value).padStart(2, '0');

const createSlot = (
    year: number,
    month: number,
    day: number,
    time: (typeof UPDATE_TIMES)[number]
): ReminderSlot => {
    const updateTimestamp = Date.UTC(
        year,
        month - 1,
        day,
        time.hour - BANGKOK_OFFSET_HOURS,
        time.minute
    );
    const reminderTimestamp = updateTimestamp - REMINDER_MINUTES_BEFORE_UPDATE * 60 * 1000;
    const reminderBangkokTime = new Date(
        reminderTimestamp + BANGKOK_OFFSET_HOURS * 60 * 60 * 1000
    );
    const reminderLabel = `${padDatePart(reminderBangkokTime.getUTCHours())}:${padDatePart(
        reminderBangkokTime.getUTCMinutes()
    )}`;
    const dateKey = `${year}-${padDatePart(month)}-${padDatePart(day)}`;
    return {
        key: `${CHECKED_SLOT_PREFIX}:${dateKey}:${reminderLabel}`,
        label: `${reminderLabel} for the ${time.label} update`,
        timestamp: reminderTimestamp
    };
};

const getNextReminderSlot = (nowTimestamp = Date.now()): ReminderSlot => {
    const bangkokNow = new Date(nowTimestamp + BANGKOK_OFFSET_HOURS * 60 * 60 * 1000);
    const year = bangkokNow.getUTCFullYear();
    const month = bangkokNow.getUTCMonth() + 1;
    const day = bangkokNow.getUTCDate();
    const todaySlots = UPDATE_TIMES.map(time => createSlot(year, month, day, time));

    const currentOrNextSlot = todaySlots.find(slot => {
        const delay = slot.timestamp - nowTimestamp;
        return delay >= -LATE_GRACE_MS && !sessionStorage.getItem(slot.key);
    });
    if (currentOrNextSlot) return currentOrNextSlot;

    const bangkokTomorrow = new Date(Date.UTC(year, month - 1, day + 1));
    return createSlot(
        bangkokTomorrow.getUTCFullYear(),
        bangkokTomorrow.getUTCMonth() + 1,
        bangkokTomorrow.getUTCDate(),
        UPDATE_TIMES[0]
    );
};

function TransportRequestReminder() {
    const toast = useToast({ duration: 7000, isClosable: true });
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isEnabled, setIsEnabled] = useState(
        () => localStorage.getItem(REMINDER_ENABLED_KEY) === 'true'
    );
    const [nextAlert, setNextAlert] = useState<string>();
    const [isTesting, setIsTesting] = useState(false);

    useEffect(() => {
        const audio = new Audio(updateSound);
        audio.preload = 'auto';
        audioRef.current = audio;

        return () => {
            audio.pause();
            audio.currentTime = 0;
            audioRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!isEnabled) {
            setNextAlert(undefined);
            return;
        }

        let isCancelled = false;
        let timerId: number | undefined;

        const scheduleNextReminder = () => {
            const slot = getNextReminderSlot();
            setNextAlert(slot.label);
            const delay = Math.max(0, slot.timestamp - Date.now());

            timerId = window.setTimeout(async () => {
                if (isCancelled) return;

                // Mark the slot before requesting so a transient API failure cannot create a retry loop.
                sessionStorage.setItem(slot.key, 'true');
                try {
                    const result = await getPendingTransportRequestUatAlert();
                    if (isCancelled) return;

                    if (result.hasPending) {
                        try {
                            if (audioRef.current) {
                                audioRef.current.currentTime = 0;
                                await audioRef.current.play();
                            }
                        } catch {
                            toast({
                                title: 'Sound was blocked by the browser',
                                description: 'Click Test sound to enable audio for this page.',
                                status: 'warning'
                            });
                        }

                        const visibleTags = result.uatTags.slice(0, 5).join(', ');
                        const remainingCount = Math.max(0, result.pendingTagCount - 5);
                        toast({
                            title: `${result.pendingTagCount} UAT tag${result.pendingTagCount === 1 ? '' : 's'} still waiting`,
                            description: `${visibleTags}${remainingCount ? ` and ${remainingCount} more` : ''}`,
                            status: 'warning'
                        });
                    }
                } catch (error) {
                    toast({
                        title: 'Unable to check UAT reminders',
                        description: error instanceof Error ? error.message : 'Please try again later.',
                        status: 'error'
                    });
                } finally {
                    if (!isCancelled) scheduleNextReminder();
                }
            }, delay);
        };

        scheduleNextReminder();
        return () => {
            isCancelled = true;
            if (timerId !== undefined) window.clearTimeout(timerId);
        };
    }, [isEnabled, toast]);

    const playTestSound = async () => {
        if (!audioRef.current) return false;
        setIsTesting(true);
        try {
            audioRef.current.currentTime = 0;
            await audioRef.current.play();
            return true;
        } catch {
            toast({
                title: 'Unable to play sound',
                description: 'Please allow audio playback in your browser and try again.',
                status: 'error'
            });
            return false;
        } finally {
            setIsTesting(false);
        }
    };

    const enableReminder = async () => {
        if (!(await playTestSound())) return;
        localStorage.setItem(REMINDER_ENABLED_KEY, 'true');
        setIsEnabled(true);
    };

    const muteReminder = () => {
        localStorage.setItem(REMINDER_ENABLED_KEY, 'false');
        setIsEnabled(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    };

    return (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                {isEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
            </div>
            <div className="min-w-[220px] flex-1">
                <div className="flex items-center gap-2 font-semibold text-gray-800">
                    UAT sound reminder
                    <Badge colorScheme={isEnabled ? 'green' : 'gray'}>{isEnabled ? 'ON' : 'OFF'}</Badge>
                </div>
                <div className="text-sm text-gray-600">
                    {isEnabled
                        ? `Next alert at ${nextAlert ?? 'calculating...'} (Bangkok time)`
                        : 'Enable once on this browser to receive scheduled alerts.'}
                </div>
            </div>
            {isEnabled ? (
                <>
                    <Button
                        size="sm"
                        leftIcon={<FaPlay />}
                        isLoading={isTesting}
                        onClick={playTestSound}
                    >
                        Test sound
                    </Button>
                    <Button size="sm" variant="outline" onClick={muteReminder}>
                        Mute
                    </Button>
                </>
            ) : (
                <Button
                    size="sm"
                    colorScheme="orange"
                    isLoading={isTesting}
                    onClick={enableReminder}
                >
                    Enable &amp; test
                </Button>
            )}
        </div>
    );
}

export default TransportRequestReminder;
